/* eslint-disable @typescript-eslint/no-empty-function */
import { CommonModule } from "@angular/common";
import {
    afterNextRender,
    Component,
    computed,
    ElementRef,
    inject,
    signal,
    viewChild,
} from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { ActivatedRoute, RouterModule } from "@angular/router";
import { Capacitor } from "@capacitor/core";
import {
    PopupDto,
    PopupService,
    SearchResponsePopupDto,
    SettingService,
} from "@malirang/api-client";
import { BottomSheetComponent } from "@malirang/app-share";
import {
    CONTENT,
    DeviceType,
    fnGetKeyByValue,
    MD_WIDTH_SIZE,
    MemberRole,
    NO_SEE_TODAY_ADVERTISE_POPUP,
    NO_SEE_TODAY_NORMAL_POPUP,
    PopupStatus,
    PopupType,
} from "@malirang/common";
import {
    LayoutStore,
    LocalStorageService,
    ModalService,
} from "@malirang/front-share";
import dayjs from "dayjs";
import { injectNavigationEnd } from "ngxtension/navigation-end";
import { tap } from "rxjs";
import { GuardComponent } from "../../guards/guard.component";
import { ActionSheetModalService } from "../../services/action-sheet-modal.service";
import PopupMobileComponent from "../pages/schedule/components/popup-mobile/popup-mobile.component";
import PopupComponent from "../pages/schedule/components/popup/popup.component";
import { CenterCompletenessComponent } from "./components/center-completeness/center-completeness.component";
import { FooterComponent } from "./components/footer/footer.component";
import { HeaderComponent } from "./components/header/header.component";
import { MenusComponent } from "./components/menus/menus.component";

export type MenuItem = {
    url: string;
    title: string;
    icon?: string;
    mobileHidden?: boolean;
    children?: MenuItem[];
    disabled?: boolean;
};

type Layout = "default" | "blank";
@Component({
    selector: "app-layout",
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        FooterComponent,
        HeaderComponent,
        BottomSheetComponent,
        CenterCompletenessComponent,
        MenusComponent,
        GuardComponent, // Router Outlet 경로
    ],
    templateUrl: "./layout.component.html",
    styleUrl: "./layout.component.scss",
})
export class LayoutComponent {
    private readonly route = inject(ActivatedRoute);
    private readonly layoutStore = inject(LayoutStore);
    private readonly popupService = inject(PopupService);
    private readonly actionSheetModalService = inject(ActionSheetModalService);
    private readonly localStorageService = inject(LocalStorageService);
    private readonly modalService = inject(ModalService);
    private readonly settingService = inject(SettingService);

    isEnabled$ = rxResource({
        params: () => ({ key: "선생님검사도구문서함사용여부" }),
        stream: ({ params }) =>
            this.settingService.settingControllerGet(params),
    });
    isEnabled = computed(
        () =>
            this.isEnabled$.value()?.value &&
            this.isEnabled$.value()?.value === "true",
    );

    layoutRef = viewChild<ElementRef<HTMLElement>>("layoutRef");
    navigationEnd$ = injectNavigationEnd();
    layout = signal<Layout>("default");
    platform = Capacitor.getPlatform();
    // SSR에서 Capacitor.isNativePlatform()은 항상 false를 반환하므로 signal로 관리
    isNativePlatform = signal(false);

    menus = computed(() =>
        this.isNativePlatform()
            ? [
                  {
                      title: "캘린더",
                      icon: "solar:calendar-search-bold-duotone",
                      url: "/schedule",
                  },
                  {
                      title: "아동업무",
                      icon: "material-symbols-light:child-care",
                      url: "/child",
                  },
                  {
                      title: "문서함",
                      icon: "solar:book-bookmark-bold-duotone",
                      url: "/document",
                  },
                  {
                      title: CONTENT,
                      icon: "solar:chat-round-like-bold-duotone",
                      url: "/content",
                  },
              ]
            : [
                  {
                      title: "캘린더",
                      icon: "solar:calendar-search-bold-duotone",
                      url: "/schedule",
                  },
                  {
                      title: "아동업무",
                      icon: "material-symbols-light:child-care",
                      url: "/child",
                  },
                  {
                      title: `검사도구`,
                      icon: "solar:ruler-pen-bold-duotone",
                      url: "/inspection",
                      disabled: !this.isEnabled(),
                  },
                  {
                      title: "문서함",
                      icon: "solar:book-bookmark-bold-duotone",
                      url: "/document",
                  },
                  {
                      title: CONTENT,
                      icon: "solar:chat-round-like-bold-duotone",
                      url: "/content",
                  },
                  {
                      title: "스토어",
                      icon: "material-symbols-light:shopping-bag",
                      url: "/store",
                      mobileHidden: true,
                  },
                  {
                      title: "센터관리",
                      icon: "solar:home-2-bold-duotone",
                      url: "/center",
                      mobileHidden: true,
                      children: [
                          { title: "센터 관리", url: "/center" },
                          { title: "수납 관리", url: "/receipt" },
                          { title: "행정 관리", url: "/administration" },
                      ],
                  },
              ],
    );

    $res = rxResource({
        params: () => ({
            pageNo: 1,
            pageSize: 9999,
            status: fnGetKeyByValue(PopupStatus, PopupStatus.SHOWING),
            targetDevices: Capacitor.isNativePlatform()
                ? [fnGetKeyByValue(DeviceType, DeviceType.MOBILE)]
                : [fnGetKeyByValue(DeviceType, DeviceType.PC)],
            targetRoles: [fnGetKeyByValue(MemberRole, MemberRole.TEACHER)],
        }),
        stream: ({ params }) =>
            this.popupService
                .popupControllerSearch({
                    ...params,
                })
                .pipe(
                    tap((res: SearchResponsePopupDto) => {
                        const adPopups = res.items.filter(
                            (item: PopupDto) =>
                                item.type ===
                                fnGetKeyByValue(PopupType, PopupType.ADVERTISE),
                        );

                        const normalPopups = res.items.filter(
                            (item: PopupDto) =>
                                item.type ===
                                fnGetKeyByValue(PopupType, PopupType.NORMAL),
                        );

                        // 팝업 열기
                        this.handleOpenPopups(adPopups, normalPopups);
                    }),
                ),
    });

    constructor() {
        afterNextRender(() => {
            // 클라이언트에서 렌더링된 후 Capacitor 플랫폼 상태를 업데이트
            this.isNativePlatform.set(Capacitor.isNativePlatform());

            const layout = this.route.snapshot.data["layout"] as Layout;
            this.layout.set(layout || "default");

            this.navigationEnd$.subscribe({
                next: () => {
                    if (window) {
                        window.scrollTo(0, 0);
                    }

                    const layout = this.route.snapshot.data["layout"] as Layout;
                    this.layout.set(layout || "default");
                },
            });
        });
    }

    /**
     * @name handleOpenPopups
     * @description 팝업 열기
     * @returns {void}
     */
    async handleOpenPopups(
        adPopups: PopupDto[],
        normalPopups: PopupDto[],
    ): Promise<void> {
        try {
            const ad: string | null = await this.localStorageService.get(
                NO_SEE_TODAY_ADVERTISE_POPUP,
            );

            if (ad && dayjs(ad).isValid() && dayjs().isBefore(dayjs(ad)))
                adPopups = [];

            const normal: string | null = await this.localStorageService.get(
                NO_SEE_TODAY_NORMAL_POPUP,
            );

            if (
                normal &&
                dayjs(normal).isValid() &&
                dayjs().isBefore(dayjs(normal))
            )
                normalPopups = [];

            const isAdPopupOpen = adPopups && adPopups?.length > 0;
            const isNormalPopupOpen = normalPopups && normalPopups?.length > 0;
            // 보여줄 팝업이 없으면 중지
            if (!isAdPopupOpen && !isNormalPopupOpen) return;

            // 네이티브
            if (Capacitor.isNativePlatform()) {
                if (isAdPopupOpen) {
                    const adModalForMobile =
                        this.actionSheetModalService.create(
                            PopupMobileComponent,
                            {
                                componentProps: {
                                    popups: adPopups,
                                    closeAll: !isNormalPopupOpen,
                                },
                            },
                        );

                    if (isNormalPopupOpen) {
                        adModalForMobile.closed.subscribe(() => {
                            this.actionSheetModalService.create(
                                PopupMobileComponent,
                                {
                                    componentProps: {
                                        popups: normalPopups,
                                    },
                                },
                            );
                        });
                    }
                }
                if (!isAdPopupOpen && isNormalPopupOpen) {
                    this.actionSheetModalService.create(PopupMobileComponent, {
                        componentProps: {
                            popups: normalPopups,
                        },
                    });
                }
            } else {
                // 웹 작은 사이즈
                if (window.innerWidth < MD_WIDTH_SIZE) {
                    if (isAdPopupOpen) {
                        const adModalForMobile =
                            this.actionSheetModalService.create(
                                PopupMobileComponent,
                                {
                                    componentProps: {
                                        popups: adPopups,
                                        closeAll: !isNormalPopupOpen,
                                    },
                                },
                            );

                        if (isNormalPopupOpen) {
                            adModalForMobile.closed.subscribe(() => {
                                this.actionSheetModalService.create(
                                    PopupMobileComponent,
                                    {
                                        componentProps: {
                                            popups: normalPopups,
                                        },
                                    },
                                );
                            });
                        }
                    }
                    if (!isAdPopupOpen && isNormalPopupOpen) {
                        this.actionSheetModalService.create(
                            PopupMobileComponent,
                            {
                                componentProps: {
                                    popups: normalPopups,
                                },
                            },
                        );
                    }
                } else {
                    // PC 및 큰 사이즈
                    const modal = this.modalService.create(PopupComponent, {
                        componentProps: {
                            adPopups,
                            normalPopups,
                        },
                    });
                    modal.closed.subscribe(() => {});
                }
            }
        } catch (error) {
            console.error("Error opening popups:", error);
        }
    }

    onLayoutScroll() {
        this.layoutStore.layoutScrollEvent$.next(
            this.layoutRef()?.nativeElement || null,
        );
    }
}
